import "./index.css";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useControlledValue } from "../../hooks/useControlledValue";
import { useDebounce } from "../../hooks/useDebounce";
import { Dropdown } from "../Dropdown";
import type { ISelectOption } from "../Select/types";
import { ComboBoxContent } from "./ComboBoxContent";
import { ComboBoxTrigger } from "./ComboBoxTrigger";
import { ComboBoxProvider } from "./context";
import type { IComboBoxContext, IComboBoxProps } from "./types";

export const ComboBox = (props: IComboBoxProps) => {
  const {
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    value,
    onChange,
    onClear,
    options = [],
    onSearch,
    disabled,
    loading,
    multiple,
    minWidth,
    maxWidth,
    minHeight,
    maxHeight = 260,
    matchTriggerWidth = true,
    debounce = onSearch ? 300 : 0,
    openLength = 2,
    id,
  } = props;

  const { value: open, onChange: onOpenChange } = useControlledValue<boolean>({
    controlledValue: controlledOpen,
    controlledOnChange: controlledOnOpenChange,
    defaultValue: false,
  });

  const generatedId = useId();
  const baseId = id ?? generatedId;
  const triggerId = `${baseId}-trigger`;
  const contentId = `${baseId}-content`;
  const inputId = props.inputId ?? `${baseId}-input`;

  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [shouldFocusInput, setShouldFocusInput] = useState(false);

  const [searchValue, setSearchValue] = useState("");
  const {
    debouncedValue: debouncedSearchValue,
    cancel: cancelDebouncedSearch,
  } = useDebounce(searchValue, debounce);

  useEffect(() => {
    if (!onSearch) return;
    onSearch(debouncedSearchValue);
  }, [debouncedSearchValue, onSearch]);

  const normalizedValue = useMemo(() => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  useEffect(() => {
    if (!multiple) {
      setSearchValue(value ? (value as ISelectOption).text : "");
    }
  }, [value, multiple]);

  useEffect(() => {
    if (shouldFocusInput && searchInputRef.current) {
      searchInputRef.current.focus();
      setShouldFocusInput(false);
    }
  }, [shouldFocusInput]);

  const filteredOptions = useMemo(() => {
    if (onSearch) return options;
    const q = searchValue.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.text.toLowerCase().startsWith(q));
  }, [options, onSearch, searchValue]);

  const handleSearchChange = useCallback(
    (newValue: string) => {
      setSearchValue(newValue);

      if (newValue && !open && newValue.length >= openLength) {
        onOpenChange(true);
      }

      if (!multiple && newValue === "" && value) {
        onChange(undefined);
        onOpenChange(false);
      }
    },
    [open, multiple, value, openLength, onOpenChange, onChange],
  );

  const handleSelect = useCallback(
    (opt: ISelectOption) => {
      if (!multiple) {
        setSearchValue(opt.text);
        onChange(opt);
        onOpenChange(false);
      } else {
        const currentValues = normalizedValue;
        const isSelected = currentValues.some((v) => v.id === opt.id);

        if (isSelected) {
          onChange(currentValues.filter((v) => v.id !== opt.id));
        } else {
          onChange([...currentValues, opt]);
        }

        setSearchValue("");
        setShouldFocusInput(true);
      }
    },
    [multiple, normalizedValue, onChange, onOpenChange],
  );

  const handleClear = useCallback(() => {
    setSearchValue("");
    onChange(multiple ? [] : undefined);
    cancelDebouncedSearch();
    onClear?.();
    searchInputRef.current?.focus();
  }, [onChange, onClear, multiple, cancelDebouncedSearch]);

  const hasValue = multiple
    ? Array.isArray(value) && value.length > 0
    : !!value;
  const showClearButton = !disabled && !loading && (hasValue || !!searchValue);

  const displayValue = useMemo(() => {
    if (!multiple) {
      return (value as ISelectOption)?.text ?? "";
    }
    return normalizedValue.map((opt) => opt.text).join(", ");
  }, [value, normalizedValue, multiple]);

  const ctxValue: IComboBoxContext = useMemo(
    () => ({
      ...props,
      inputId,
      contentId,
      hasValue,
      displayValue,
      searchValue,
      setSearchValue: handleSearchChange,
      onSelect: handleSelect,
      onClear: handleClear,
      filteredOptions,
      showClearButton,
      maxHeight,
      searchInputRef,
      multiple,
    }),
    [
      props,
      inputId,
      contentId,
      hasValue,
      displayValue,
      searchValue,
      handleSearchChange,
      handleSelect,
      handleClear,
      filteredOptions,
      showClearButton,
      maxHeight,
      multiple,
    ],
  );

  return (
    <ComboBoxProvider value={ctxValue}>
      <Dropdown
        trigger={<ComboBoxTrigger />}
        content={<ComboBoxContent />}
        triggerId={triggerId}
        contentId={contentId}
        role="listbox"
        open={open}
        onOpenChange={onOpenChange}
        disabled={disabled}
        interactions={["dismiss", "listNavigation"]}
        placement="bottom-start"
        offset={8}
        floatingFocusManager={{
          initialFocus: -1,
          modal: false,
          returnFocus: true,
          restoreFocus: false,
        }}
        roleOptions={{
          enabled: false,
        }}
        listNavigationOptions={{ virtual: true }}
        matchTriggerWidth={matchTriggerWidth}
        minWidth={minWidth}
        maxWidth={maxWidth}
        minHeight={minHeight}
        maxHeight={maxHeight}
      />
    </ComboBoxProvider>
  );
};
